import React, { useState } from 'react'
import { useLocation } from 'wouter'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'

interface InterestTopic {
  id: string
  label: string
  emoji: string
  description: string
  color: string
  category: 'nature' | 'technology' | 'social' | 'adventure' | 'mystery' | 'creative'
}

// Organized by categories for progressive disclosure
const interestCategories = {
  tech: [
    { id: 'tech', label: 'Tech & Code', emoji: '💻', description: 'Hackers, systems, and networks', color: 'bg-gradient-to-br from-blue-50 to-cyan-100 border-blue-300', category: 'tech' as const }
  ],
  mystery: [
    { id: 'mystery', label: 'Mystery Solving', emoji: '🔍', description: 'Detective work and solving cases', color: 'bg-gradient-to-br from-indigo-50 to-purple-100 border-indigo-300', category: 'mystery' as const }
  ],
  action: [
    { id: 'action', label: 'Action & Combat', emoji: '⚡', description: 'Powerful action and battles', color: 'bg-gradient-to-br from-orange-50 to-red-100 border-orange-300', category: 'action' as const }
  ],
  scifi: [
    { id: 'sci-fi', label: 'Sci-Fi & Space', emoji: '🚀', description: 'Space and alien worlds', color: 'bg-gradient-to-br from-purple-50 to-pink-100 border-purple-300', category: 'scifi' as const }
  ],
  survival: [
    { id: 'survival', label: 'Survival & Wilderness', emoji: '🔥', description: 'Extreme adventures', color: 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-300', category: 'survival' as const }
  ]
}

const InterestSelectionPage: React.FC = () => {

  const [, setLocation] = useLocation()

  const brainState = localStorage.getItem('current-brain-state') || 'focused'

  const handleTopicSelect = (topicId: string) => {
    localStorage.setItem('selected-interest', topicId)
    setLocation(`/stories/${topicId}`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-green-50 p-3 sm:p-4">
      <div className="max-w-4xl mx-auto py-4 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">📚</div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-indigo-900 mb-3 sm:mb-4 px-2">
            What Kind of Story Sounds Fun?
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-indigo-700 leading-relaxed max-w-2xl mx-auto mb-4 sm:mb-6 px-4">
            Pick a theme that excites you!
          </p>
        </div>
  
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Object.values(interestCategories).flat().map((topic) => (
            <Card
              key={topic.id}
              className={`cursor-pointer transition-all duration-300 border-2 hover:scale-102 hover:shadow-md active:scale-98 ${topic.color} min-h-[160px] sm:min-h-[180px]`}
              onClick={() => handleTopicSelect(topic.id)}
            >
              <CardContent className="p-4 sm:p-6 text-center h-full flex flex-col justify-center">
                <div className="text-4xl sm:text-5xl md:text-6xl mb-3 sm:mb-4">{topic.emoji}</div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-indigo-900 mb-2 sm:mb-3">{topic.label}</h3>
                <p className="text-sm sm:text-base text-indigo-700">{topic.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
  
}

export default InterestSelectionPage